import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
} from "react";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import "./SakhaChatbot.css";
import { sakhaChatbotUrl } from "../../ServerAPI/serverAPI";
import VijnataLogo from "../../assets/images/vijnata_logo_1.png"
/* ─── Static payload fields ──────────────────────────────── */
const PAYLOAD_DEFAULTS = {
    collection_name: "docs_openai_acca",
    org_id: "6d09d92f-25a1-4dcf-ac0e-7873dde3d2c6",
    filename: "Financial_Reporting_Taxation.pdf",
    top_k: 5,
};

/* ─── Suggestion chips ────────────────────────────────────── */
const SUGGESTIONS = [
    "Explain deferred tax",
    "What is IFRS 15?",
    "Explain under- and over-provisions",
    "What is tax losses?"
];

/* ─── Helpers ────────────────────────────────────────────── */
const formatTime = (date) =>
    date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

/* ─── Icons (inline SVG) ─────────────────────────────────── */
const BotIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="10" rx="2" />
        <circle cx="12" cy="5" r="2" />
        <path d="M12 7v4" />
        <line x1="8" y1="16" x2="8" y2="16" strokeWidth="3" />
        <line x1="16" y1="16" x2="16" y2="16" strokeWidth="3" />
    </svg>
);

const SendIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="22" y1="2" x2="11" y2="13" />
        <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
);

const CloseIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const UserIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </svg>
);

const AlertIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
);

const WifiOffIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="1" y1="1" x2="23" y2="23" />
        <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
        <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
        <path d="M10.71 5.05A16 16 0 0 1 22.56 9" />
        <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
        <line x1="12" y1="20" x2="12.01" y2="20" />
    </svg>
);

/* ─── Connection states ──────────────────────────────────── */
const CONN = {
    IDLE: "idle",
    CONNECTING: "connecting",
    CONNECTED: "connected",
    DISCONNECTED: "disconnected",
    ERROR: "error",
};

/* ═══════════════════════════════════════════════════════════
   Sakha Chatbot Component
   ═══════════════════════════════════════════════════════════ */
const SakhaChatbot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [connState, setConnState] = useState(CONN.IDLE);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [unread, setUnread] = useState(0);
    const [studentType, setStudentType] = useState("Learner");

    const wsRef = useRef(null);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const reconnectTimerRef = useRef(null);
    const reconnectAttempts = useRef(0);

    /* ── Scroll to bottom ─────────────────────────────────── */
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, []);

    useEffect(() => {
        if (isOpen) scrollToBottom();
    }, [messages, isTyping, isOpen, scrollToBottom]);

    /* ── Add message helper ───────────────────────────────── */
    const addMessage = useCallback((role, text, isError = false) => {
        const msg = { id: Date.now() + Math.random(), role, text, time: new Date(), isError };
        setMessages((prev) => [...prev, msg]);
        if (role === "bot" && !isOpen) {
            setUnread((n) => n + 1);
        }
    }, [isOpen]);

    /* ── WebSocket connection ─────────────────────────────── */
    const connectWS = useCallback(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) return;

        setConnState(CONN.CONNECTING);
        const ws = new WebSocket(sakhaChatbotUrl());
        wsRef.current = ws;

        ws.onopen = () => {
            setConnState(CONN.CONNECTED);
            reconnectAttempts.current = 0;
        };

        ws.onmessage = (event) => {
            setIsTyping(false);
            try {
                const data = JSON.parse(event.data);
                const text =
                    data.answer ||
                    data.response ||
                    data.message ||
                    data.content ||
                    JSON.stringify(data);
                addMessage("bot", text);
            } catch {
                // Plain text response
                addMessage("bot", event.data);
            }
        };

        ws.onerror = () => {
            setIsTyping(false);
            setConnState(CONN.ERROR);
        };

        ws.onclose = (e) => {
            setIsTyping(false);
            setConnState(CONN.DISCONNECTED);
            wsRef.current = null;

            // Auto-reconnect with back-off (max 5 attempts)
            if (reconnectAttempts.current < 5 && isOpen) {
                const delay = Math.min(3000 * 2 ** reconnectAttempts.current, 30000);
                reconnectAttempts.current += 1;
                reconnectTimerRef.current = setTimeout(connectWS, delay);
            }
        };
    }, [addMessage, isOpen]);

    /* ── Open / close window ──────────────────────────────── */
    const openChat = useCallback(() => {
        setUnread(0);
        setIsOpen(true);
        setIsClosing(false);
    }, []);

    const closeChat = useCallback(() => {
        setIsClosing(true);
        setTimeout(() => {
            setIsOpen(false);
            setIsClosing(false);
        }, 220);
    }, []);

    const toggleChat = useCallback(() => {
        if (isOpen) {
            closeChat();
        } else {
            openChat();
        }
    }, [isOpen, openChat, closeChat]);

    /* ── Connect on first open ────────────────────────────── */
    useEffect(() => {
        if (isOpen && connState === CONN.IDLE) {
            connectWS();
        }
    }, [isOpen, connState, connectWS]);

    /* ── Cleanup on unmount ───────────────────────────────── */
    useEffect(() => {
        return () => {
            clearTimeout(reconnectTimerRef.current);
            wsRef.current?.close();
        };
    }, []);

    /* ── Auto-grow textarea ───────────────────────────────── */
    const handleInputChange = (e) => {
        setInput(e.target.value);
        const ta = e.target;
        ta.style.height = "auto";
        ta.style.height = Math.min(ta.scrollHeight, 120) + "px";
    };

    /* ── Send message ─────────────────────────────────────── */
    const sendMessage = useCallback(
        (text) => {
            const trimmed = (text || input).trim();
            if (!trimmed) return;

            if (
                !wsRef.current ||
                wsRef.current.readyState !== WebSocket.OPEN
            ) {
                addMessage(
                    "bot",
                    "I'm not connected right now. Please wait while I reconnect…",
                    true
                );
                connectWS();
                return;
            }

            // Add user message to UI
            addMessage("user", trimmed);
            setInput("");
            if (textareaRef.current) {
                textareaRef.current.style.height = "auto";
            }

            // Show typing indicator
            setIsTyping(true);

            // Send payload
            const payload = {
                query: trimmed,
                student_type: studentType,
                ...PAYLOAD_DEFAULTS,
            };
            wsRef.current.send(JSON.stringify(payload));
        },
        [input, addMessage, connectWS, studentType]
    );

    /* ── Key handler ──────────────────────────────────────── */
    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    /* ── Connection status label ──────────────────────────── */
    const connLabel = {
        [CONN.IDLE]: "Initialising…",
        [CONN.CONNECTING]: "Connecting…",
        [CONN.CONNECTED]: "Online",
        [CONN.DISCONNECTED]: "Reconnecting…",
        [CONN.ERROR]: "Connection error",
    }[connState];

    const connDotClass = {
        [CONN.IDLE]: "offline",
        [CONN.CONNECTING]: "connecting",
        [CONN.CONNECTED]: "",
        [CONN.DISCONNECTED]: "connecting",
        [CONN.ERROR]: "offline",
    }[connState];

    const isSendDisabled =
        !input.trim() ||
        isTyping ||
        connState === CONN.CONNECTING ||
        connState === CONN.IDLE;

    /* ─────────────────────────────────────────────────────── */
    return (
        <>
            {/* ── Chat window ─────────────────────────────────── */}
            {(isOpen || isClosing) && (
                <div
                    id="sakha-chat-window"
                    className={`sakha-window ${isClosing ? "closing" : ""}`}
                    role="dialog"
                    aria-label="Sakha Chatbot"
                >
                    {/* Header */}
                    <div className="sakha-header">
                        <div className="sakha-header-avatar">
                            <BotIcon />
                        </div>
                        <div className="sakha-header-info">
                            <div className="sakha-header-title-row">
                                <h6>Sakha</h6>
                                <select
                                    value={studentType}
                                    onChange={(e) => setStudentType(e.target.value)}
                                    className="sakha-student-type-select"
                                    aria-label="Select student type"
                                >
                                    <option value="Learner">Learner</option>
                                    <option value="Achiever">Achiever</option>
                                    <option value="Star">Star</option>
                                </select>
                            </div>
                            <span>
                                <span className={`sakha-status-dot ${connDotClass}`} />
                                {connLabel} &nbsp;·&nbsp; Your Study Assistant
                            </span>
                        </div>
                        <button
                            id="sakha-close-btn"
                            className="sakha-header-close"
                            onClick={closeChat}
                            aria-label="Close chatbot"
                        >
                            <CloseIcon />
                        </button>
                    </div>

                    {/* Connection banner */}
                    {connState !== CONN.CONNECTED && (
                        <div
                            className={`sakha-conn-banner ${connState === CONN.CONNECTING ||
                                connState === CONN.DISCONNECTED
                                ? "connecting"
                                : connState === CONN.ERROR
                                    ? "disconnected"
                                    : "connecting"
                                }`}
                        >
                            {connState === CONN.ERROR ? (
                                <>
                                    <AlertIcon />
                                    Failed to connect. Check that the server is running.
                                </>
                            ) : (
                                <>
                                    <WifiOffIcon />
                                    {connState === CONN.DISCONNECTED
                                        ? "Reconnecting to server…"
                                        : "Establishing connection…"}
                                </>
                            )}
                        </div>
                    )}

                    {/* Messages */}
                    <div className="sakha-messages" id="sakha-messages-area">
                        {/* Welcome / empty state */}
                        {messages.length === 0 && (
                            <>
                                <div className="sakha-welcome">
                                    <div className="sakha-welcome-icon">
                                        <BotIcon />
                                    </div>
                                    <h5>Hi, I'm Sakha! 👋</h5>
                                    <p>
                                        Your ACCA study assistant.
                                    </p>
                                </div>
                                <div className="sakha-suggestions">
                                    {SUGGESTIONS.map((s) => (
                                        <button
                                            key={s}
                                            id={`sakha-chip-${s.replace(/\s+/g, "-").toLowerCase()}`}
                                            className="sakha-chip"
                                            onClick={() => sendMessage(s)}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}

                        {/* Message list */}
                        {messages.map((msg) => (
                            <div
                                key={msg.id}
                                className={`sakha-msg ${msg.role}`}
                            >
                                <div className="sakha-msg-avatar">
                                    {msg.role === "bot" ? <BotIcon /> : <UserIcon />}
                                </div>
                                <div>
                                    {msg.isError ? (
                                        <div className="sakha-error-msg">
                                            <AlertIcon />
                                            {msg.text}
                                        </div>
                                    ) : (
                                        <div className="sakha-bubble">
                                            {msg.role === "bot" ? (
                                                <div className="sakha-markdown">
                                                    <ReactMarkdown
                                                        remarkPlugins={[remarkMath]}
                                                        rehypePlugins={[rehypeKatex]}
                                                    >
                                                        {msg.text}
                                                    </ReactMarkdown>
                                                </div>
                                            ) : (
                                                msg.text
                                            )}
                                            <span className="sakha-bubble-time">
                                                {formatTime(msg.time)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}

                        {/* Typing indicator */}
                        {isTyping && (
                            <div className="sakha-typing">
                                <div
                                    className="sakha-msg-avatar"
                                    style={{
                                        background: "linear-gradient(135deg,#23479F,#3a6fd8)",
                                        borderRadius: "50%",
                                        width: 28,
                                        height: 28,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#fff",
                                        flexShrink: 0,
                                    }}
                                >
                                    <BotIcon />
                                </div>
                                <div className="sakha-typing-bubble">
                                    <div className="sakha-dot" />
                                    <div className="sakha-dot" />
                                    <div className="sakha-dot" />
                                </div>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input area */}
                    <div className="sakha-input-area">
                        <div className="sakha-input-wrap">
                            <textarea
                                id="sakha-input"
                                ref={textareaRef}
                                className="sakha-textarea"
                                rows={1}
                                placeholder="Ask Sakha anything…"
                                value={input}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                aria-label="Chat input"
                            />
                        </div>
                        <button
                            id="sakha-send-btn"
                            className="sakha-send-btn"
                            onClick={() => sendMessage()}
                            disabled={isSendDisabled}
                            aria-label="Send message"
                        >
                            <SendIcon />
                        </button>
                    </div>

                    {/* Footer */}
                    <div className="sakha-footer">
                        Powered by <span><img src={VijnataLogo} alt="" height={20} width={100} /></span>
                    </div>
                </div>
            )}

            {/* ── FAB trigger button ───────────────────────────── */}
            <button
                id="sakha-fab"
                className={`sakha-fab ${isOpen ? "open" : ""}`}
                onClick={toggleChat}
                aria-label={isOpen ? "Close Sakha chatbot" : "Open Sakha chatbot"}
                aria-expanded={isOpen}
            >
                {/* Bot icon when closed, X when open */}
                {isOpen ? (
                    <CloseIcon />
                ) : (
                    <BotIcon />
                )}

                {/* Unread badge */}
                {!isOpen && unread > 0 && (
                    <span className="sakha-badge" aria-label={`${unread} unread messages`}>
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </button>
        </>
    );
};

export default SakhaChatbot;
